
import { buildSpatialPlan } from '../spatial/snap-engine.js?v=20260817-pcieanchor10';
import { evaluateHardwareEligibility } from './hardware-eligibility.js';
import { componentSlotForTrackType } from './component-slots.js';
import { stableSolveFingerprint } from './solve-state.js';

const TYPES = Object.freeze({
  gpu: { label:'GPU', dataset:'gpus' },
  aircooler: { label:'AIR', dataset:'coolers' },
  radiator: { label:'AIO', dataset:'aios' },
  psu: { label:'PSU', dataset:'psus' },
  motherboard: { label:'BOARD', dataset:'motherboards' }
});

const MAX_PREFILTER = 28;
const MAX_PROSPECTIVE_SOLVES = 12;
const MAX_RESULTS = 5;
const VALIDATION_CACHE_LIMIT = 320;
const validationCache = new Map();

function finite(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function candidateIdentity(type, row) {
  const brand = String(row?.brand || row?.manufacturer || '').trim().toLowerCase();
  const model = String(row?.model || row?.name || row?.displayLabel || row?.gpu || row?.cooler || row?.aio || '').trim().toLowerCase();
  const dims = [finite(row?.l), finite(row?.w), finite(row?.h)].map(v => v.toFixed(2)).join('x');
  const extra = type === 'radiator' ? finite(row?.radiatorSize) : type === 'psu' ? String(row?.formFactor || '') : type === 'motherboard' ? String(row?.formFactor || '') : '';
  return `${type}|${brand}|${model}|${dims}|${extra}`;
}

function rememberValidation(key, value) {
  if (validationCache.has(key)) validationCache.delete(key);
  validationCache.set(key, value);
  while (validationCache.size > VALIDATION_CACHE_LIMIT) {
    validationCache.delete(validationCache.keys().next().value);
  }
}

function candidateDims(row) {
  return { l:finite(row?.l), w:finite(row?.w), h:finite(row?.h) };
}

function candidateMeta(type, row) {
  if (type === 'gpu') {
    return {
      dimensionKnown:row?.dimensionKnown || null,
      gpuHeightSource:row?.gpuHeightSource || 'catalog_width'
    };
  }
  if (type === 'radiator') {
    return {
      radiatorSize:finite(row?.radiatorSize),
      fanProfile:row?.fanProfile || row?.radFan || 'standard',
      radFan:row?.radFan || row?.fanProfile || 'standard',
      fanThickness:finite(row?.fanThickness,25),
      fanSize:finite(row?.fanSize),
      fanCount:finite(row?.fanCount),
      aioBlock:row?.aioBlock ? { ...row.aioBlock, dimensionKnown:{ ...(row.aioBlock.dimensionKnown||{}) } } : null,
      tubeLength:finite(row?.tubeLength),
      tubeLengthKnown:!!row?.tubeLengthKnown,
      pumpLocation:row?.pumpLocation || '',
      dimensionKnown:row?.dimensionKnown || null,
      dimensionSource:row?.dimensionSource || 'unknown'
    };
  }
  if (type === 'motherboard') {
    return {
      formFactor:row?.formFactor || 'ITX',
      brand:row?.brand || '', model:row?.model || '', socket:row?.socket || '', chipset:row?.chipset || '',
      dimensionKnown:row?.dimensionKnown || null, dimensionSource:row?.dimensionSource || 'unknown'
    };
  }
  if (type === 'psu') {
    return {
      formFactor:row?.formFactor || 'SFX',
      brand:row?.brand || '', model:row?.model || '', wattage:finite(row?.wattage),
      dimensionKnown:row?.dimensionKnown || null, dimensionSource:row?.dimensionSource || 'unknown'
    };
  }
  return { dimensionKnown:row?.dimensionKnown || null, dimensionSource:row?.dimensionSource || 'unknown' };
}

function hasUsefulDimensions(type, row) {
  const d = candidateDims(row);
  if (type === 'gpu') return d.l > 0 && d.w > 0 && d.h > 0;
  if (type === 'aircooler') return d.l > 0 && d.w > 0 && d.h > 0;
  if (type === 'radiator') return d.l > 0 && d.w > 0 && d.h > 0 && finite(row?.radiatorSize) > 0;
  if (type === 'motherboard' || type === 'psu') return d.l > 0 && d.w > 0 && d.h > 0;
  return false;
}

function cloneSolverObject(comp) {
  if (!comp?.isComponent) return null;
  if (comp.trackType === 'fan') {
    return {
      isComponent:true,
      trackType:'fan',
      uid:String(comp.uid || ''),
      attachedToRadiatorUid:comp.attachedToRadiatorUid || null,
      assignedSlotIndex:Number.isInteger(Number(comp.assignedSlotIndex)) ? Number(comp.assignedSlotIndex) : null,
      autoFabricatedFan:!!comp.autoFabricatedFan,
      nativeSpecs:{
        l:finite(comp.nativeSpecs?.l), w:finite(comp.nativeSpecs?.w), h:finite(comp.nativeSpecs?.h)
      },
      componentMeta:{ ...(comp.componentMeta || {}) },
      isLockedToCase:false,
      spatialUserAdjusted:false
    };
  }
  if (!componentSlotForTrackType(comp.trackType)) return null;
  return {
    isComponent:true,
    trackType:String(comp.trackType || ''),
    uid:String(comp.uid || ''),
    displayLabel:String(comp.displayLabel || comp.badgeEl?.textContent || comp.trackType || ''),
    nativeSpecs:{
      l:finite(comp.nativeSpecs?.l), w:finite(comp.nativeSpecs?.w), h:finite(comp.nativeSpecs?.h)
    },
    componentMeta:{ ...(comp.componentMeta || {}) },
    isLockedToCase:false,
    spatialUserAdjusted:false
  };
}

function prospectiveActiveBoxes(activeBoxes, type, row) {
  const slot = componentSlotForTrackType(type);
  const cloned = [];
  const removedRadiatorUids = new Set();

  for (const comp of activeBoxes || []) {
    if (!comp?.isComponent) continue;
    if (componentSlotForTrackType(comp.trackType) === slot) {
      if (comp.trackType === 'radiator' && comp.uid) removedRadiatorUids.add(comp.uid);
      continue;
    }
    const copy = cloneSolverObject(comp);
    if (copy) cloned.push(copy);
  }

  if (removedRadiatorUids.size) {
    for (let i = cloned.length - 1; i >= 0; i--) {
      if (cloned[i]?.trackType === 'fan' && removedRadiatorUids.has(cloned[i].attachedToRadiatorUid)) cloned.splice(i,1);
    }
  }

  const dims = candidateDims(row);
  cloned.push({
    isComponent:true,
    trackType:type,
    uid:`nearfit_${candidateIdentity(type,row)}`,
    displayLabel:String(row?.displayLabel || row?.model || row?.name || type),
    nativeSpecs:dims,
    componentMeta:candidateMeta(type,row),
    isLockedToCase:false,
    spatialUserAdjusted:false
  });
  return cloned;
}

function currentReference(activeBoxes, type) {
  const slot = componentSlotForTrackType(type);
  if (!slot) return null;
  const current = [...(activeBoxes || [])].reverse().find(comp => comp?.isComponent && componentSlotForTrackType(comp.trackType) === slot);
  if (!current) return null;
  return {
    trackType:type,
    label:String(current.displayLabel || current.badgeEl?.textContent || current.trackType || ''),
    dims:{ l:finite(current.nativeSpecs?.l), w:finite(current.nativeSpecs?.w), h:finite(current.nativeSpecs?.h) },
    meta:{ ...(current.componentMeta || {}) },
    source:'installed'
  };
}

function activeLimits(plan) {
  return plan?.effective?.validationLimits || plan?.effective?.limits || {};
}

function dimensionDistance(a, b, weights = [1,1,1]) {
  const av=[finite(a?.l),finite(a?.w),finite(a?.h)], bv=[finite(b?.l),finite(b?.w),finite(b?.h)];
  let total=0, used=0;
  for(let i=0;i<3;i++){
    if(!(av[i]>0) || !(bv[i]>0)) continue;
    const scale=Math.max(1,bv[i]);
    total += Math.abs(av[i]-bv[i])/scale * weights[i];
    used += weights[i];
  }
  return used ? total/used : 1;
}

function quickScore(type, row, plan, reference = null) {
  const dims=candidateDims(row);
  const limits=activeLimits(plan);
  let score=0;

  if (reference?.dims) {
    if (type === 'gpu') score = 1000 - dimensionDistance(dims, reference.dims, [2.4,2.0,1.5]) * 700;
    else if (type === 'aircooler') score = 1000 - dimensionDistance(dims, reference.dims, [0.8,0.8,3.0]) * 650;
    else if (type === 'radiator') {
      const targetRad=finite(reference.meta?.radiatorSize);
      const rad=finite(row?.radiatorSize);
      const radDistance=targetRad>0&&rad>0 ? Math.abs(rad-targetRad)/Math.max(120,targetRad) : 0.5;
      score=1000-radDistance*760-dimensionDistance(dims,reference.dims,[1.2,1.0,0.5])*220;
    } else if (type === 'psu') {
      score=700-dimensionDistance(dims,reference.dims,[1,1,1.6])*350;
      if (String(row?.formFactor||'').toUpperCase()===String(reference.meta?.formFactor||'').toUpperCase()) score+=260;
      const targetW=finite(reference.meta?.wattage), watts=finite(row?.wattage);
      if(targetW>0&&watts>0)score+=Math.max(0,90-Math.abs(watts-targetW)/10);
    } else if (type === 'motherboard') {
      score=650-dimensionDistance(dims,reference.dims,[1.4,1.4,0.1])*250;
      if (String(row?.formFactor||'').toUpperCase()===String(reference.meta?.formFactor||'').toUpperCase()) score+=260;
      if (reference.meta?.socket && String(row?.socket||'').toLowerCase()===String(reference.meta.socket).toLowerCase()) score+=120;
      if (reference.meta?.chipset && String(row?.chipset||'').toLowerCase()===String(reference.meta.chipset).toLowerCase()) score+=70;
    }
  } else {
    if (type === 'gpu') {
      const ratios=[];
      const maxL=finite(limits.maxGPU),maxT=finite(limits.maxGPUT ?? limits.maxThick),maxH=finite(limits.maxGPUH);
      if(maxL>0&&dims.l>0)ratios.push(dims.l/maxL);
      if(maxT>0&&dims.w>0)ratios.push(dims.w/maxT);
      if(maxH>0&&dims.h>0)ratios.push(dims.h/maxH);
      score=(ratios.length?ratios.reduce((a,b)=>a+b,0)/ratios.length:0)*1000;
    } else if (type === 'aircooler') {
      const max=finite(limits.maxCPU); score=max>0?clamp(dims.h/max,0,1.25)*1000:dims.h;
    } else if (type === 'radiator') {
      const max=finite(limits.maxAIO),rad=finite(row?.radiatorSize); score=max>0?clamp(rad/max,0,1.25)*1000:rad;
    } else if (type === 'psu') {
      const order={FLEX:1,SFX:2,'SFX-L':3,ATX:4};
      score=(order[String(row?.formFactor||'').toUpperCase()]||0)*180 + finite(row?.wattage)/10 + dims.h;
    } else if (type === 'motherboard') {
      const order={ITX:1,DTX:2,MATX:3,ATX:4};
      score=(order[String(row?.formFactor||'').toUpperCase()]||0)*220 + (dims.l*dims.w)/10000;
    }
  }

  if (type === 'gpu') {
    const known=row?.dimensionKnown;
    if(known && (!known.l || !known.w || !known.h)) score-=300;
  }
  if (type === 'psu' && row?.dimensionSource === 'form_factor_nominal') score-=70;
  return score;
}

function rowEligibility(type, row, context) {
  return evaluateHardwareEligibility({
    trackType:type,
    dims:candidateDims(row),
    meta:candidateMeta(type,row),
    plan:context.plan,
    caseState:context.caseBox?.spatialState || null
  });
}

function candidateValidityFromPlan(type, plan) {
  if (!plan?.hasHardValidSolution) return false;
  const record=plan.componentValidity?.[type];
  if(record?.valid===false)return false;
  if(type==='aircooler')return true;
  const candidates=plan.candidates?.[type] || [];
  return candidates.length>0;
}

function validationSummary(type, row, plan) {
  const limits=activeLimits(plan);
  const dims=candidateDims(row);
  const details=[];
  if(type==='gpu'){
    const l=finite(limits.maxGPU),t=finite(limits.maxGPUT ?? limits.maxThick),h=finite(limits.maxGPUH);
    if(l>0)details.push(`L +${Math.max(0,l-dims.l).toFixed(0)}mm`);
    if(t>0)details.push(`T +${Math.max(0,t-dims.w).toFixed(0)}mm`);
    if(h>0)details.push(`H +${Math.max(0,h-dims.h).toFixed(0)}mm`);
  } else if(type==='aircooler'){
    const max=finite(limits.maxCPU); if(max>0)details.push(`CPU +${Math.max(0,max-dims.h).toFixed(0)}mm`);
  } else if(type==='radiator'){
    const max=finite(limits.maxAIO),rad=finite(row?.radiatorSize); if(max>0&&rad>0)details.push(`RAD +${Math.max(0,max-rad).toFixed(0)}mm`);
  } else if(type==='psu'){
    details.push(String(row?.formFactor || 'PSU').toUpperCase());
    if(finite(row?.wattage)>0)details.push(`${finite(row.wattage).toFixed(0)}W`);
    if(row?.dimensionSource==='form_factor_nominal')details.push('NOMINAL SIZE');
  } else if(type==='motherboard'){
    details.push(String(row?.formFactor || 'BOARD').toUpperCase());
    if(row?.socket)details.push(String(row.socket));
    if(row?.chipset)details.push(String(row.chipset));
  }
  const family=String(plan?.layoutFamily || '').toUpperCase();
  const variant=String(plan?.layoutVariant || '').replace(/_/g,' ').toUpperCase();
  const topology=variant && variant!=='STANDARD' ? `${family} · ${variant}` : family;
  return { details, topology };
}

async function yieldToBrowser() {
  if (typeof requestIdleCallback === 'function') {
    await new Promise(resolve => requestIdleCallback(() => resolve(), { timeout:32 }));
  } else {
    await new Promise(resolve => setTimeout(resolve, 0));
  }
}

async function validateCandidate(type, row, context, fingerprint) {
  const key=`${fingerprint}|${candidateIdentity(type,row)}`;
  if(validationCache.has(key)){
    const cached=validationCache.get(key);
    validationCache.delete(key); validationCache.set(key,cached);
    return { ...cached, cacheHit:true };
  }
  const boxes=prospectiveActiveBoxes(context.activeBoxes,type,row);
  const plan=buildSpatialPlan(
    context.caseBox?.catalogRecord || context.caseBox?.nativeSpecs || {},
    context.caseBox?.structuredOverlay || null,
    context.caseBox?.spatialState || null,
    boxes
  );
  const valid=candidateValidityFromPlan(type,plan);
  // Cache only the compact verdict/presentation summary. Full solver plans can carry many
  // candidate/envelope objects and would turn a recommendation cache into a memory cache.
  const compact={
    valid,
    summary:valid ? validationSummary(type,row,plan) : null
  };
  rememberValidation(key,compact);
  return { ...compact, cacheHit:false };
}

function createText(tag, className, text) {
  const el=document.createElement(tag);
  if(className)el.className=className;
  el.textContent=text;
  return el;
}

export function createNearFitController({ root, getContext, getDatasets, labelFor, onChoose } = {}) {
  if (!root) return null;
  const status=root.querySelector('[data-near-fit-status]');
  const resultsEl=root.querySelector('[data-near-fit-results]');
  const tabs=[...root.querySelectorAll('[data-near-fit-type]')];
  const refreshBtn=root.querySelector('[data-near-fit-refresh]');
  let mode='workbench';
  let activeType='gpu';
  let requestEpoch=0;
  let buildVersion=0;
  let lastFailure=null;
  let lastFailureSignature='';
  let lastResultBuildVersion=-1;
  let lastResultType='';
  const stats={requests:0,prospectiveSolves:0,prospectiveSolveMsTotal:0,lastProspectiveSolveMs:0,validationCacheHits:0,lastRequestMs:0,lastCandidateCount:0};

  const setStatus=(text,state='idle')=>{
    if(status){status.textContent=text;status.dataset.state=state;}
  };
  const setActiveTab=(type)=>{
    if(!TYPES[type])return;
    activeType=type;
    tabs.forEach(btn=>btn.classList.toggle('active',btn.dataset.nearFitType===type));
  };
  const clearResults=(message='Choose a category to find hard-verified alternatives.')=>{
    if(!resultsEl)return;
    resultsEl.innerHTML='';
    resultsEl.appendChild(createText('div','sff-near-fit-empty',message));
  };

  function contextNow(){
    const ctx=typeof getContext==='function'?getContext():null;
    return ctx && ctx.caseBox && ctx.plan ? ctx : null;
  }

  function referenceFor(type,context){
    if(lastFailure?.trackType===type)return lastFailure;
    return currentReference(context?.activeBoxes,type);
  }

  function renderResults(type, rows, reference){
    if(!resultsEl)return;
    resultsEl.innerHTML='';
    if(!rows.length){clearResults('No hard-verified near-fit alternatives found for this build state.');return;}
    const refText=reference?.label ? `Closest to ${reference.label}` : 'Closest to available case clearance';
    resultsEl.appendChild(createText('div','sff-near-fit-reference',refText));
    rows.forEach(result=>{
      const card=document.createElement('div'); card.className='sff-near-fit-result';
      const top=document.createElement('div'); top.className='sff-near-fit-result-top';
      const label=createText('span','sff-near-fit-result-label',result.label);
      const button=createText('button','sff-near-fit-load','LOAD'); button.type='button';
      button.addEventListener('click',()=>{
        const ok=typeof onChoose==='function' ? onChoose(type,result.row,result.label) : false;
        if(ok!==false){ lastFailure=null; setStatus('Loaded · build will re-solve','ready'); }
      });
      top.append(label,button); card.appendChild(top);
      if(result.summary.details.length)card.appendChild(createText('div','sff-near-fit-metrics',result.summary.details.join(' · ')));
      const topology=result.summary.topology ? `${result.summary.topology} · HARD VERIFIED` : 'HARD VERIFIED';
      card.appendChild(createText('div','sff-near-fit-topology',topology));
      resultsEl.appendChild(card);
    });
  }

  async function run(type=activeType,{automatic=false}={}){
    if(!TYPES[type])return;
    setActiveTab(type);
    const context=contextNow();
    if(mode!=='simulator'){setStatus('Simulator mode only','idle');clearResults('Switch to Simulator to use Near Fit.');return;}
    if(!context){setStatus('Add a case first','idle');clearResults('Near Fit activates when a simulator case has a solved topology.');return;}
    const datasets=typeof getDatasets==='function'?getDatasets():{};
    const dataset=datasets?.[TYPES[type].dataset] || [];
    if(!dataset.length){setStatus('Catalog unavailable','error');clearResults('No catalog records are loaded for this hardware type.');return;}

    const started=(typeof performance!=='undefined'&&performance.now)?performance.now():Date.now();
    const epoch=++requestEpoch;
    stats.requests++;
    setStatus(automatic?'Finding alternatives…':'Scanning catalog…','working');
    if(resultsEl){resultsEl.innerHTML='';resultsEl.appendChild(createText('div','sff-near-fit-empty','Filtering candidates…'));}

    const reference=referenceFor(type,context);
    const referenceIdentity=reference?.label?.toLowerCase().trim() || '';
    const pool=[];
    for(const row of dataset){
      if(!row || !hasUsefulDimensions(type,row))continue;
      const label=String(labelFor?.(row) || row.displayLabel || row.model || row.name || TYPES[type].label).trim();
      if(referenceIdentity && label.toLowerCase()===referenceIdentity)continue;
      const eligibility=rowEligibility(type,row,context);
      if(!eligibility.allowed)continue;
      pool.push({row,label,score:quickScore(type,row,context.plan,reference)});
    }
    pool.sort((a,b)=>b.score-a.score);
    const shortlist=pool.slice(0,MAX_PREFILTER);
    stats.lastCandidateCount=shortlist.length;
    if(epoch!==requestEpoch)return;
    if(!shortlist.length){setStatus('No eligible candidates','idle');clearResults('No catalog candidates pass the current case-state prefilter.');return;}

    const fingerprint=stableSolveFingerprint(context.caseBox,context.activeBoxes || []);
    const verified=[];
    const solveLimit=Math.min(MAX_PROSPECTIVE_SOLVES,shortlist.length);
    for(let i=0;i<solveLimit && verified.length<MAX_RESULTS;i++){
      if(epoch!==requestEpoch)return;
      setStatus(`Verifying ${i+1}/${solveLimit}…`,'working');
      await yieldToBrowser();
      if(epoch!==requestEpoch)return;
      const candidate=shortlist[i];
      const solveStarted=(typeof performance!=='undefined'&&performance.now)?performance.now():Date.now();
      const validated=await validateCandidate(type,candidate.row,context,fingerprint);
      const solveElapsed=((typeof performance!=='undefined'&&performance.now)?performance.now():Date.now())-solveStarted;
      if(validated.cacheHit)stats.validationCacheHits++;
      else {
        stats.prospectiveSolves++;
        stats.lastProspectiveSolveMs=solveElapsed;
        stats.prospectiveSolveMsTotal+=solveElapsed;
      }
      if(validated.valid){
        verified.push({
          ...candidate,
          summary:validated.summary || {details:[],topology:''}
        });
      }
    }
    if(epoch!==requestEpoch)return;
    verified.sort((a,b)=>b.score-a.score);
    renderResults(type,verified,reference);
    const ended=(typeof performance!=='undefined'&&performance.now)?performance.now():Date.now();
    stats.lastRequestMs=ended-started;
    lastResultBuildVersion=buildVersion;
    lastResultType=type;
    setStatus(verified.length?`${verified.length} verified fits`:'No verified fits',verified.length?'ready':'idle');
  }

  tabs.forEach(btn=>btn.addEventListener('click',()=>run(btn.dataset.nearFitType)));
  refreshBtn?.addEventListener('click',()=>run(activeType));
  setActiveTab(activeType);
  clearResults();

  return {
    setMode(nextMode){
      mode=nextMode;
      root.hidden=mode!=='simulator';
      if(mode==='simulator'){
        const ctx=contextNow();
        setStatus(ctx?'Choose hardware type':'Add a case first','idle');
      }
    },
    notifyCatalogReady(){
      if(mode==='simulator'&&contextNow())setStatus('Choose hardware type','idle');
    },
    notifyBuildChanged(){
      buildVersion++;
      requestEpoch++;
      const ctx=contextNow();
      if(mode!=='simulator')return;
      if(!ctx){setStatus('Add a case first','idle');clearResults('Near Fit activates when a simulator case has a solved topology.');return;}
      if(lastResultBuildVersion>=0){
        setStatus('Build changed · refresh','stale');
        clearResults(`Build changed. Re-run ${TYPES[lastResultType || activeType]?.label || 'Near Fit'} for current topology.`);
      } else setStatus('Choose hardware type','idle');
    },
    reportFailure(failure={}){
      if(!TYPES[failure.trackType])return;
      lastFailure={
        trackType:failure.trackType,
        label:String(failure.label || TYPES[failure.trackType].label),
        dims:{ l:finite(failure.dims?.l),w:finite(failure.dims?.w),h:finite(failure.dims?.h) },
        meta:{ ...(failure.meta || {}) },
        reason:failure.reason || '', source:'failed'
      };
      setActiveTab(failure.trackType);
      if(mode==='simulator'&&contextNow()){
        setStatus('Finding closest verified alternatives…','working');
        Promise.resolve().then(()=>run(failure.trackType,{automatic:true}));
      }
    },
    clearFailure(){ lastFailure=null; },
    run,
    getStats(){return {...stats,averageProspectiveSolveMs:stats.prospectiveSolves?stats.prospectiveSolveMsTotal/stats.prospectiveSolves:0,validationCacheSize:validationCache.size,activeType,buildVersion};}
  };
}
